import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AnalyticsProps } from '@inplayer-org/inplayer-ui';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight } from 'react-icons/fa';

// hooks
import useWillMount from 'hooks/useWillMount';

// components
import PageButton from './PageButton';

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0.5em;
  justify-content: center;
`;

interface ButtonsGrid {
  columnCount: number;
}

const ButtonsGrid = styled.div<ButtonsGrid>`
  ${({ columnCount }) => `
    display: grid;
    grid-template-columns: repeat(${columnCount}, 1fr);
    justify-content: center;
    column-gap: 5px;
  `}
`;

interface OwnProps {
  totalItems: number;
  itemsPerPage: number;
  startPage?: number;
  onPageChange: (pageNum: number) => void;
  isDisabled: boolean;
}

type Props = OwnProps & AnalyticsProps;

const DropdownPagination: FC<Props> = ({ tag, totalItems, itemsPerPage, startPage = 1, onPageChange, isDisabled }) => {
  const [currentPage, setCurrentPage] = useState(startPage);

  const totalPageCount = useMemo(() => Math.ceil(totalItems / itemsPerPage), [totalItems, itemsPerPage]);

  const onPageClick = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    onPageChange(pageNumber);
  }, []);

  useWillMount(() => {
    onPageChange(startPage);
  });

  const visiblePageButtons = useMemo(() => {
    if (totalPageCount < 3) {
      return [1, 2].slice(0, totalPageCount);
    }

    if (currentPage === 1) {
      return [currentPage, currentPage + 1, currentPage + 2];
    }

    if (currentPage === totalPageCount) {
      return [currentPage - 2, currentPage - 1, currentPage];
    }

    return [currentPage - 1, currentPage, currentPage + 1];
  }, [currentPage, totalPageCount]);

  const columnCount = useMemo(() => (totalPageCount <= 3 ? totalPageCount : visiblePageButtons.length + 4), [
    totalPageCount,
    visiblePageButtons.length,
  ]);

  if (totalPageCount < 2) {
    return null;
  }

  return (
    <MainContainer>
      <ButtonsGrid columnCount={columnCount}>
        {totalPageCount > 3 && (
          <>
            <PageButton
              tag={`${tag}-page-first`}
              title="First page"
              isDisabled={isDisabled || currentPage === 1}
              page={1}
              onClick={onPageClick}
            >
              <FaAngleDoubleLeft />
            </PageButton>
            <PageButton
              tag={`${tag}-page-previous`}
              title="Previous page"
              isDisabled={isDisabled || currentPage === 1}
              page={currentPage - 1}
              onClick={onPageClick}
            >
              <FaAngleLeft />
            </PageButton>
          </>
        )}
        {visiblePageButtons.map((pageNum: number, i: number) => (
          <PageButton
            key={i}
            tag={`${tag}-page-${pageNum}`}
            page={pageNum}
            isSelected={pageNum === currentPage}
            title={pageNum === currentPage ? 'Reload current page' : ''}
            isDisabled={isDisabled}
            onClick={onPageClick}
          >
            {pageNum}
          </PageButton>
        ))}
        {totalPageCount > 3 && (
          <>
            <PageButton
              tag={`${tag}-page-next`}
              title="Next page"
              isDisabled={isDisabled || currentPage === totalPageCount}
              page={currentPage + 1}
              onClick={onPageClick}
            >
              <FaAngleRight />
            </PageButton>
            <PageButton
              tag={`${tag}-page-last`}
              title="Last page"
              isDisabled={isDisabled || currentPage === totalPageCount}
              page={totalPageCount}
              onClick={onPageClick}
            >
              <FaAngleDoubleRight />
            </PageButton>
          </>
        )}
      </ButtonsGrid>
    </MainContainer>
  );
};

export default DropdownPagination;
